/**
 * Word-style ribbon for any TipTap editor inside Domivus.
 *
 * Tabs:
 *   ── Start ──── Zwischenablage · Schriftart · Absatz
 *   ── Einfügen ─ Tabelle · Bild · Link · Sonderzeichen · Umbruch
 *   ── Layout ─── Seite einrichten · Absatz (Einzug + Abstand)
 *
 *   <WordRibbon
 *      editor={tiptapInstance}
 *      layout={{                       // page-level metadata (per template)
 *        marginTop, marginBottom, marginLeft, marginRight,
 *        orientation, pageFormat, columns,
 *      }}
 *      onLayoutChange={(patch) => …}   // merges into the layout object
 *   />
 */
import { useState, useRef, useEffect, useCallback } from "react";
import React from "react";
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  TextAlignLeft, TextAlignCenter, TextAlignRight, TextAlignJustify,
  ListBullets, ListNumbers, ListChecks, TextHOne, TextHTwo, TextHThree,
  Eraser, PaintBrushHousehold,
  TextSubscript, TextSuperscript, TextAa, Highlighter, Palette,
  Link as LinkIcon, Image as ImageIcon, Table as TableIcon, Minus,
  FileArrowDown, Scissors, Copy, Clipboard,
  Quotes, ArrowsVertical, TextIndent, TextOutdent,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const FONT_FAMILIES = [
  "Arial", "Calibri", "Cambria", "Courier New", "Georgia",
  "Helvetica", "Tahoma", "Times New Roman", "Verdana",
];
const FONT_SIZES = ["8", "9", "10", "11", "12", "14", "16", "18", "20", "22", "24", "28", "32", "36", "48", "72"];
const COLORS = [
  "#000000", "#1f2937", "#374151", "#6b7280", "#9ca3af",
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981",
  "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#ec4899",
];
const HIGHLIGHTS = [
  null, "#fef08a", "#fde68a", "#fbbf24", "#fca5a5", "#f87171",
  "#86efac", "#6ee7b7", "#67e8f9", "#93c5fd", "#a5b4fc", "#d8b4fe",
];
const LINE_HEIGHTS = ["1", "1.15", "1.5", "2", "2.5", "3"];
const PAGE_FORMATS = [
  { key: "a4", label: "A4 (210 × 297 mm)" },
  { key: "a5", label: "A5 (148 × 210 mm)" },
  { key: "letter", label: "Letter (216 × 279 mm)" },
  { key: "legal", label: "Legal (216 × 356 mm)" },
];

export default function WordRibbon({ editor, layout, onLayoutChange, snippets, onInsertSnippet, onOpenSnippetPicker, categories, category, onCategoryChange }) {
  const [tab, setTab] = useState("start");

  if (!editor) {
    return (
      <div className="border-b border-slate-200 bg-white h-[112px] shrink-0" />
    );
  }

  return (
    <div className="border-b border-slate-200 bg-white shrink-0" data-testid="word-ribbon">
      {/* Tab bar */}
      <div className="flex items-center gap-0 px-3 border-b border-slate-100">
        {[
          { key: "start", label: "Start" },
          { key: "insert", label: "Einfügen" },
          { key: "layout", label: "Layout" },
          // Sprint 5j — Vorlagen/Snippets als eigener Tab statt separater Zeile darüber
          { key: "snippets", label: "Vorlagen" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-200"
            }`}
            data-testid={`ribbon-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-2 py-1.5 min-h-[80px] flex items-stretch gap-2 overflow-x-auto">
        {tab === "start" && <StartTab editor={editor} />}
        {tab === "insert" && <InsertTab editor={editor} />}
        {tab === "layout" && (
          <LayoutTab editor={editor} layout={layout || {}} onLayoutChange={onLayoutChange || (() => {})} />
        )}
        {tab === "snippets" && (
          <SnippetsTab
            snippets={snippets || []}
            onInsert={onInsertSnippet || (() => {})}
            onOpenSnippetPicker={onOpenSnippetPicker}
            categories={categories || []}
            category={category}
            onCategoryChange={onCategoryChange || (() => {})}
          />
        )}
      </div>
    </div>
  );
}


/* =====================================================================
   SNIPPETS TAB  (überarbeitet — Suche, Kategorie, Baustein-Button)
   ===================================================================== */
function SnippetsTab({ snippets, onInsert, onOpenSnippetPicker, categories, category, onCategoryChange }) {
  const [q, setQ] = useState("");
  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return snippets;
    return snippets.filter((s) => (
      ((s.name || "") + " " + (s.title || "") + " " + (s.category || "") + " " + (s.subject || ""))
        .toLowerCase().includes(query)
    ));
  }, [q, snippets]);

  return (
    <div className="flex items-stretch gap-3 w-full">
      {/* Prominent: Bausteine einfügen (öffnet SnippetPicker mit Snippets/Templates/Vertragsbausteinen) */}
      {onOpenSnippetPicker && (
        <button
          onClick={onOpenSnippetPicker}
          className="flex flex-col items-center justify-center gap-1 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors shrink-0 min-w-[120px]"
          data-testid="ribbon-open-snippet-picker"
          title="Baustein oder Vorlage einfügen (an Cursor-Position)"
        >
          <span className="text-lg">＋</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider">Bausteine</span>
        </button>
      )}

      <div className="flex flex-col gap-1 min-w-0 flex-1">
        {/* Kopfzeile: Suche + Kategorie */}
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Vorlagen suchen…"
            className="h-7 text-xs border border-slate-200 rounded px-2 bg-white focus:outline-none focus:border-slate-400 w-48"
            data-testid="ribbon-snippet-search"
          />
          {categories && categories.length > 0 && (
            <select
              className="h-7 text-xs border border-slate-200 rounded px-2 bg-white"
              value={category || ""}
              onChange={(e) => onCategoryChange(e.target.value)}
              data-testid="ribbon-snippet-category"
            >
              <option value="">Alle Kategorien</option>
              {categories.map((c) => (
                <option key={c.value || c} value={c.value || c}>{c.label || c}</option>
              ))}
            </select>
          )}
          <span className="text-[10px] text-slate-500 tabular-nums">({filtered.length} Vorlagen)</span>
          <span className="text-[10px] text-slate-500 italic ml-auto">Klick ⇒ ersetzt Editorinhalt (nach Rückfrage)</span>
        </div>
        {/* Quick-Buttons für die (gefilterten) Vorlagen */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {filtered.length === 0 && (
            <span className="text-xs text-slate-400 italic px-2">
              {q ? "Keine Treffer" : 'Keine Vorlagen — anlegen unter „Stammdaten → Vorlagen".'}
            </span>
          )}
          {filtered.slice(0, 12).map((s) => (
            <button
              key={s.id}
              onClick={() => onInsert(s)}
              className="text-xs px-2.5 py-1 bg-slate-50 hover:bg-slate-900 hover:text-white border border-slate-200 rounded transition-colors"
              title={s.subject || s.title || s.name}
              data-testid={`ribbon-snippet-${s.id}`}
            >
              {s.title || s.name || "Vorlage"}
            </button>
          ))}
          {filtered.length > 12 && (
            <button
              onClick={onOpenSnippetPicker}
              className="text-xs px-2.5 py-1 text-emerald-700 hover:text-emerald-900 font-semibold"
              data-testid="ribbon-more-templates"
            >
              +{filtered.length - 12} weitere …
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* =====================================================================
   START TAB
   ===================================================================== */

function StartTab({ editor }) {
  const cs = editor.getAttributes("textStyle");
  const curFont = cs.fontFamily || "";
  const curSize = (cs.fontSize || "").replace("pt", "") || "";
  const curColor = cs.color || "";

  // clipboard helpers — best-effort, browser API
  const copy = useCallback(() => {
    const { from, to } = editor.state.selection;
    if (from === to) return;
    const text = editor.state.doc.textBetween(from, to, " ");
    navigator.clipboard?.writeText(text);
    toast.success("Kopiert");
  }, [editor]);
  const cut = useCallback(() => {
    const { from, to } = editor.state.selection;
    if (from === to) return;
    const text = editor.state.doc.textBetween(from, to, " ");
    navigator.clipboard?.writeText(text);
    editor.chain().focus().deleteSelection().run();
  }, [editor]);
  const paste = useCallback(async () => {
    try {
      const t = await navigator.clipboard?.readText();
      if (t) editor.chain().focus().insertContent(t).run();
    } catch {
      toast.info("Bitte Strg+V im Editor verwenden");
    }
  }, [editor]);

  return (
    <>
      {/* Group: Zwischenablage */}
      <Group label="Zwischenablage">
        <div className="flex items-center gap-0.5">
          <Btn onClick={paste} label="Einfügen" testid="rb-paste" big>
            <Clipboard size={20} />
          </Btn>
          <div className="flex flex-col gap-0.5">
            <Btn onClick={cut} label="Ausschneiden" testid="rb-cut">
              <Scissors size={13} />
            </Btn>
            <Btn onClick={copy} label="Kopieren" testid="rb-copy">
              <Copy size={13} />
            </Btn>
            <Btn
              onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
              label="Formatierung übertragen / löschen"
              testid="rb-clear-format"
            >
              <PaintBrushHousehold size={13} />
            </Btn>
          </div>
        </div>
      </Group>

      {/* Group: Schriftart */}
      <Group label="Schriftart">
        <div className="flex items-center gap-1">
          <select
            value={curFont}
            onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
            className="h-7 border border-slate-200 rounded-sm px-1.5 text-xs bg-white w-32"
            data-testid="rb-font-family"
            title="Schriftart"
          >
            <option value="">(Standard)</option>
            {FONT_FAMILIES.map((f) => (
              <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
            ))}
          </select>
          <select
            value={curSize}
            onChange={(e) =>
              e.target.value
                ? editor.chain().focus().setFontSize(`${e.target.value}pt`).run()
                : editor.chain().focus().unsetFontSize().run()
            }
            className="h-7 border border-slate-200 rounded-sm px-1.5 text-xs bg-white w-14"
            data-testid="rb-font-size"
            title="Schriftgröße"
          >
            <option value="">—</option>
            {FONT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Btn
            onClick={() => stepFontSize(editor, +1)}
            label="Schrift vergrößern" testid="rb-font-up"
          >
            <span className="text-sm font-semibold leading-none">A<sup className="text-[8px]">+</sup></span>
          </Btn>
          <Btn
            onClick={() => stepFontSize(editor, -1)}
            label="Schrift verkleinern" testid="rb-font-down"
          >
            <span className="text-xs leading-none">A<sup className="text-[8px]">−</sup></span>
          </Btn>
          <ChangeCaseMenu editor={editor} />
          <Btn
            onClick={() => editor.chain().focus().unsetAllMarks().run()}
            label="Formatierung löschen" testid="rb-unset-marks"
          >
            <Eraser size={14} />
          </Btn>
        </div>
        <div className="flex items-center gap-0.5 mt-1">
          <Btn onClick={() => editor.chain().focus().toggleBold().run()}
               active={editor.isActive("bold")} label="Fett (Strg+B)" testid="rb-bold">
            <TextB size={14} weight="bold" />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleItalic().run()}
               active={editor.isActive("italic")} label="Kursiv (Strg+I)" testid="rb-italic">
            <TextItalic size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleUnderline().run()}
               active={editor.isActive("underline")} label="Unterstrichen (Strg+U)" testid="rb-underline">
            <TextUnderline size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleStrike().run()}
               active={editor.isActive("strike")} label="Durchgestrichen" testid="rb-strike">
            <TextStrikethrough size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleSubscript().run()}
               active={editor.isActive("subscript")} label="Tiefgestellt" testid="rb-sub">
            <TextSubscript size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleSuperscript().run()}
               active={editor.isActive("superscript")} label="Hochgestellt" testid="rb-sup">
            <TextSuperscript size={14} />
          </Btn>
          <Sep />
          <ColorPicker
            label="Textfarbe" current={curColor} icon={<Palette size={14} />} testid="rb-color"
            colors={COLORS}
            onPick={(c) =>
              c
                ? editor.chain().focus().setColor(c).run()
                : editor.chain().focus().unsetColor().run()
            }
          />
          <ColorPicker
            label="Hervorhebung" icon={<Highlighter size={14} />} testid="rb-highlight"
            colors={HIGHLIGHTS}
            onPick={(c) =>
              c
                ? editor.chain().focus().setHighlight({ color: c }).run()
                : editor.chain().focus().unsetHighlight().run()
            }
          />
        </div>
      </Group>

      {/* Group: Absatz */}
      <Group label="Absatz">
        <div className="flex items-center gap-0.5">
          <Btn onClick={() => editor.chain().focus().toggleBulletList().run()}
               active={editor.isActive("bulletList")} label="Aufzählung" testid="rb-ul">
            <ListBullets size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()}
               active={editor.isActive("orderedList")} label="Nummerierung" testid="rb-ol">
            <ListNumbers size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleTaskList?.().run?.()}
               label="Aufgaben-Liste (nicht aktiviert)" testid="rb-tasklist" disabled>
            <ListChecks size={14} />
          </Btn>
          <Btn
            onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
            label="Einzug erhöhen" testid="rb-indent"
          >
            <TextIndent size={14} />
          </Btn>
          <Btn
            onClick={() => editor.chain().focus().liftListItem("listItem").run()}
            label="Einzug verringern" testid="rb-outdent"
          >
            <TextOutdent size={14} />
          </Btn>
          <Sep />
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
               active={editor.isActive("heading", { level: 1 })} label="Überschrift 1" testid="rb-h1">
            <TextHOne size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
               active={editor.isActive("heading", { level: 2 })} label="Überschrift 2" testid="rb-h2">
            <TextHTwo size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
               active={editor.isActive("heading", { level: 3 })} label="Überschrift 3" testid="rb-h3">
            <TextHThree size={14} />
          </Btn>
        </div>
        <div className="flex items-center gap-0.5 mt-1">
          <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()}
               active={editor.isActive({ textAlign: "left" })} label="Linksbündig" testid="rb-al-left">
            <TextAlignLeft size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign("center").run()}
               active={editor.isActive({ textAlign: "center" })} label="Zentriert" testid="rb-al-center">
            <TextAlignCenter size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()}
               active={editor.isActive({ textAlign: "right" })} label="Rechtsbündig" testid="rb-al-right">
            <TextAlignRight size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign("justify").run()}
               active={editor.isActive({ textAlign: "justify" })} label="Blocksatz" testid="rb-al-justify">
            <TextAlignJustify size={14} />
          </Btn>
          <Sep />
          <LineHeightMenu editor={editor} />
          <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()}
               active={editor.isActive("blockquote")} label="Zitat" testid="rb-quote">
            <Quotes size={14} />
          </Btn>
        </div>
      </Group>
    </>
  );
}


/* =====================================================================
   INSERT TAB
   ===================================================================== */

function InsertTab({ editor }) {
  const inTable = editor.isActive("table");
  const insertTable = () =>
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  const insertImage = () => {
    const url = window.prompt("Bild-URL eingeben:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };
  const insertLink = () => {
    const url = window.prompt("Link-URL:", editor.getAttributes("link").href || "https://");
    if (url == null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const insertSymbol = (s) => editor.chain().focus().insertContent(s).run();

  return (
    <>
      <Group label="Tabellen">
        <div className="flex items-center gap-1">
          <Btn onClick={insertTable} label="Tabelle einfügen" testid="rb-insert-table" big>
            <TableIcon size={20} />
          </Btn>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-0.5">
              <Btn
                onClick={() => editor.chain().focus().addColumnBefore().run()}
                disabled={!inTable} label="Spalte links einfügen" testid="rb-tbl-col-before"
              >
                <span className="text-[10px] font-semibold">⇤ Sp</span>
              </Btn>
              <Btn
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                disabled={!inTable} label="Spalte rechts einfügen" testid="rb-tbl-col-after"
              >
                <span className="text-[10px] font-semibold">Sp ⇥</span>
              </Btn>
              <Btn
                onClick={() => editor.chain().focus().deleteColumn().run()}
                disabled={!inTable} label="Spalte löschen" testid="rb-tbl-col-del"
              >
                <span className="text-[10px] font-semibold text-rose-600">−Sp</span>
              </Btn>
            </div>
            <div className="flex items-center gap-0.5">
              <Btn
                onClick={() => editor.chain().focus().addRowBefore().run()}
                disabled={!inTable} label="Zeile darüber einfügen" testid="rb-tbl-row-before"
              >
                <span className="text-[10px] font-semibold">↑ Z</span>
              </Btn>
              <Btn
                onClick={() => editor.chain().focus().addRowAfter().run()}
                disabled={!inTable} label="Zeile darunter einfügen" testid="rb-tbl-row-after"
              >
                <span className="text-[10px] font-semibold">Z ↓</span>
              </Btn>
              <Btn
                onClick={() => editor.chain().focus().deleteRow().run()}
                disabled={!inTable} label="Zeile löschen" testid="rb-tbl-row-del"
              >
                <span className="text-[10px] font-semibold text-rose-600">−Z</span>
              </Btn>
            </div>
            <div className="flex items-center gap-0.5">
              <Btn
                onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                disabled={!inTable} label="Kopfzeile umschalten" testid="rb-tbl-header"
              >
                <span className="text-[10px] font-semibold">⤒</span>
              </Btn>
              <Btn
                onClick={() => editor.chain().focus().mergeOrSplit().run()}
                disabled={!inTable} label="Zellen verbinden/teilen" testid="rb-tbl-merge"
              >
                <span className="text-[10px] font-semibold">⇔</span>
              </Btn>
              <Btn
                onClick={() => editor.chain().focus().deleteTable().run()}
                disabled={!inTable} label="Tabelle löschen" testid="rb-tbl-delete"
              >
                <span className="text-[10px] font-semibold text-rose-600">✕</span>
              </Btn>
            </div>
          </div>
        </div>
      </Group>
      <Group label="Illustrationen">
        <Btn onClick={insertImage} label="Bild über URL einfügen" testid="rb-insert-image" big>
          <ImageIcon size={20} />
        </Btn>
      </Group>
      <Group label="Links">
        <Btn onClick={insertLink} label="Link einfügen/bearbeiten" testid="rb-insert-link" big>
          <LinkIcon size={20} />
        </Btn>
      </Group>
      <Group label="Symbole">
        <div className="grid grid-cols-6 gap-0.5 text-xs">
          {["§", "€", "©", "®", "™", "°", "—", "–", "·", "→", "←", "✓"].map((c) => (
            <button
              key={c}
              onClick={() => insertSymbol(c)}
              className="h-6 w-6 border border-slate-200 rounded-sm hover:bg-slate-100"
              data-testid={`rb-symbol-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </Group>
      <Group label="Umbruch">
        <div className="flex items-center gap-1">
          <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()}
               label="Horizontale Linie" testid="rb-hr">
            <Minus size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().insertPageBreak().run()}
               label="Seitenumbruch" testid="rb-pagebreak">
            <FileArrowDown size={14} />
          </Btn>
        </div>
      </Group>
    </>
  );
}


/* =====================================================================
   LAYOUT TAB
   ===================================================================== */

function LayoutTab({ editor, layout, onLayoutChange }) {
  // Paragraph-level (current paragraph)
  const pAttrs = editor.getAttributes("paragraph");
  const setParAttr = (key, val) => {
    editor.chain().focus().updateAttributes("paragraph", { [key]: val }).run();
    editor.chain().focus().updateAttributes("heading", { [key]: val }).run();
  };

  return (
    <>
      <Group label="Seite einrichten">
        <div className="grid grid-cols-4 gap-1 text-[10px]">
          <NumField label="Rand oben (mm)" value={layout.marginTop ?? 25}
                    onChange={(v) => onLayoutChange({ marginTop: v })}
                    testid="rb-page-margin-top" />
          <NumField label="Rand unten (mm)" value={layout.marginBottom ?? 25}
                    onChange={(v) => onLayoutChange({ marginBottom: v })}
                    testid="rb-page-margin-bottom" />
          <NumField label="Rand links (mm)" value={layout.marginLeft ?? 25}
                    onChange={(v) => onLayoutChange({ marginLeft: v })}
                    testid="rb-page-margin-left" />
          <NumField label="Rand rechts (mm)" value={layout.marginRight ?? 25}
                    onChange={(v) => onLayoutChange({ marginRight: v })}
                    testid="rb-page-margin-right" />
        </div>
        <div className="flex items-center gap-2 mt-1">
          <label className="text-[10px] text-slate-600">Ausrichtung
            <select
              value={layout.orientation || "portrait"}
              onChange={(e) => onLayoutChange({ orientation: e.target.value })}
              className="h-6 ml-1 border border-slate-200 rounded-sm px-1 text-xs bg-white"
              data-testid="rb-orientation"
            >
              <option value="portrait">Hochformat</option>
              <option value="landscape">Querformat</option>
            </select>
          </label>
          <label className="text-[10px] text-slate-600">Format
            <select
              value={layout.pageFormat || "a4"}
              onChange={(e) => onLayoutChange({ pageFormat: e.target.value })}
              className="h-6 ml-1 border border-slate-200 rounded-sm px-1 text-xs bg-white"
              data-testid="rb-page-format"
            >
              {PAGE_FORMATS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </label>
          <label className="text-[10px] text-slate-600">Spalten
            <select
              value={layout.columns ?? 1}
              onChange={(e) => onLayoutChange({ columns: parseInt(e.target.value) })}
              className="h-6 ml-1 border border-slate-200 rounded-sm px-1 text-xs bg-white"
              data-testid="rb-columns"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </label>
          <label className="text-[10px] text-slate-600 inline-flex items-center gap-1">
            <input type="checkbox" checked={!!layout.hyphenation}
                   onChange={(e) => onLayoutChange({ hyphenation: e.target.checked })}
                   data-testid="rb-hyphenation" />
            Silbentrennung
          </label>
        </div>
      </Group>

      <Group label="Absatz">
        <div className="grid grid-cols-2 gap-1 text-[10px]">
          <NumField label="Einzug links (cm)" step={0.25} value={pAttrs.indentLeft ?? 0}
                    onChange={(v) => setParAttr("indentLeft", v || null)}
                    testid="rb-par-indent-left" />
          <NumField label="Einzug rechts (cm)" step={0.25} value={pAttrs.indentRight ?? 0}
                    onChange={(v) => setParAttr("indentRight", v || null)}
                    testid="rb-par-indent-right" />
          <NumField label="Abstand vor (pt)" value={pAttrs.spaceBefore ?? 0}
                    onChange={(v) => setParAttr("spaceBefore", v || null)}
                    testid="rb-par-space-before" />
          <NumField label="Abstand nach (pt)" value={pAttrs.spaceAfter ?? 0}
                    onChange={(v) => setParAttr("spaceAfter", v || null)}
                    testid="rb-par-space-after" />
        </div>
      </Group>
    </>
  );
}


/* =====================================================================
   Building blocks
   ===================================================================== */

function Group({ label, children }) {
  return (
    <div className="flex flex-col items-stretch px-2 py-0.5 border-r border-slate-100 min-w-fit">
      <div className="flex-1 flex flex-col justify-center">{children}</div>
      <div className="text-[9px] text-slate-400 uppercase tracking-wider text-center mt-1 select-none">
        {label}
      </div>
    </div>
  );
}

function Btn({ onClick, active, children, label, testid, big, disabled }) {
  return (
    <button
      type="button"
      // iter137 — onMouseDown + preventDefault: verhindert Fokus-Verlust im
      // TipTap-Editor beim Klick auf Toolbar-Buttons. Sonst „springt" der
      // Cursor raus und Bold/Italic/Align greifen nicht auf die aktuelle
      // Auswahl.
      onMouseDown={(e) => {
        if (disabled) return;
        e.preventDefault();
        onClick?.(e);
      }}
      onTouchStart={(e) => {
        if (disabled) return;
        e.preventDefault();
        onClick?.(e);
      }}
      title={label}
      data-testid={testid}
      disabled={disabled}
      className={`${big ? "p-2" : "p-1"} rounded-sm transition-colors inline-flex items-center justify-center disabled:opacity-30 ${
        active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div className="w-px h-5 bg-slate-200 mx-1" />;
}

function NumField({ label, value, onChange, step = 1, testid }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</span>
      <input
        type="number"
        step={step}
        value={value ?? 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="h-6 border border-slate-200 rounded-sm px-1.5 text-xs bg-white w-20"
        data-testid={testid}
      />
    </label>
  );
}

function ColorPicker({ label, current, icon, colors, onPick, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        title={label}
        data-testid={testid}
        className="p-1 rounded-sm text-slate-600 hover:bg-slate-100 inline-flex items-center"
      >
        {icon}
        {current && (
          <span
            className="ml-0.5 w-2 h-2 rounded-sm border border-slate-300"
            style={{ background: current }}
          />
        )}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 bg-white border border-slate-200 rounded-md shadow-lg p-2 grid grid-cols-6 gap-1">
          {colors.map((c, i) => (
            <button
              key={i}
              onClick={() => { onPick(c); setOpen(false); }}
              className="w-5 h-5 rounded-sm border border-slate-200 hover:scale-110 transition-transform"
              style={{ background: c || "transparent",
                       backgroundImage: c ? undefined :
                         "linear-gradient(45deg, transparent 45%, #ef4444 45%, #ef4444 55%, transparent 55%)" }}
              title={c || "Entfernen"}
              data-testid={`${testid}-${c || "none"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChangeCaseMenu({ editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);

  const apply = (mode) => {
    setOpen(false);
    const { from, to } = editor.state.selection;
    if (from === to) {
      toast.info("Text markieren");
      return;
    }
    const text = editor.state.doc.textBetween(from, to, " ");
    let out = text;
    if (mode === "upper") out = text.toUpperCase();
    else if (mode === "lower") out = text.toLowerCase();
    else if (mode === "title") out = text.replace(/\b\w/g, (m) => m.toUpperCase());
    else if (mode === "sentence")
      out = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    else if (mode === "toggle")
      out = [...text].map((ch) =>
        ch === ch.toUpperCase() ? ch.toLowerCase() : ch.toUpperCase()
      ).join("");
    editor.chain().focus().insertContentAt({ from, to }, out).run();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        title="Groß-/Kleinschreibung"
        data-testid="rb-change-case"
        className="p-1 rounded-sm text-slate-600 hover:bg-slate-100 inline-flex items-center text-xs"
      >
        <TextAa size={14} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 bg-white border border-slate-200 rounded-md shadow-lg min-w-[180px] text-xs">
          {[
            ["sentence", "Erster Buchstabe groß"],
            ["lower", "klein­schreibung"],
            ["upper", "GROSSSCHREIBUNG"],
            ["title", "Erster Buchstabe Jedes Worts"],
            ["toggle", "gROß/kLEIN UMKEHREN"],
          ].map(([k, lab]) => (
            <button
              key={k}
              onClick={() => apply(k)}
              className="w-full text-left px-3 py-1.5 hover:bg-slate-100"
              data-testid={`rb-case-${k}`}
            >
              {lab}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LineHeightMenu({ editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);

  const set = (val) => {
    setOpen(false);
    editor.chain().focus().updateAttributes("paragraph", { lineHeight: val }).run();
    editor.chain().focus().updateAttributes("heading", { lineHeight: val }).run();
  };
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        title="Zeilen- und Absatzabstand"
        data-testid="rb-line-height"
        className="p-1 rounded-sm text-slate-600 hover:bg-slate-100 inline-flex items-center"
      >
        <ArrowsVertical size={14} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 bg-white border border-slate-200 rounded-md shadow-lg min-w-[80px] text-xs">
          {LINE_HEIGHTS.map((v) => (
            <button
              key={v}
              onClick={() => set(parseFloat(v))}
              className="w-full text-left px-3 py-1 hover:bg-slate-100"
              data-testid={`rb-lh-${v}`}
            >
              {v}
            </button>
          ))}
          <button
            onClick={() => set(null)}
            className="w-full text-left px-3 py-1 hover:bg-slate-100 border-t border-slate-100"
          >
            Standard
          </button>
        </div>
      )}
    </div>
  );
}


/* =====================================================================
   Helpers
   ===================================================================== */

function stepFontSize(editor, delta) {
  const cur = editor.getAttributes("textStyle").fontSize;
  const n = cur ? parseFloat(cur) : 11;
  const next = Math.max(6, Math.min(120, n + delta));
  editor.chain().focus().setFontSize(`${next}pt`).run();
}
